import {GitHubAvatar} from '@github-ui/github-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {Link as RouterLink} from '@github-ui/react-router'
import {Label, Link, Truncate} from '@primer/react'
import {LockIcon, MirrorIcon, RepoTemplateIcon} from '@primer/octicons-react'
import type {HeaderResponse, RepoPaths, RepoIdentity} from '../types'

function RepoLeadingVisual({
  ownerLogin,
  ownerAvatarUrl,
  isPrivate,
  isTemplate,
}: Pick<RepoIdentity, 'ownerAvatarUrl' | 'isPrivate' | 'isTemplate'> & {ownerLogin: string}) {
  if (isTemplate) {
    return isPrivate ? (
      <LockIcon className="color-fg-muted mr-2 d-none d-md-block flex-shrink-0" />
    ) : (
      <RepoTemplateIcon className="color-fg-muted mr-2 d-none d-md-block flex-shrink-0" />
    )
  }

  return (
    <GitHubAvatar
      src={ownerAvatarUrl}
      size={24}
      square={false}
      alt={ownerLogin}
      className="mr-2 d-none d-md-block flex-shrink-0"
    />
  )
}

export type RepoTitleProps = Pick<HeaderResponse, 'repo' | 'repoName' | 'ownerLogin'> & {
  paths: Pick<RepoPaths, 'ownerProfilePath' | 'repoUrl'>
}

/** Repository title with owner/name links, visibility badge, and fork parent info. */
export function RepoTitle({repo, repoName, paths, ownerLogin}: RepoTitleProps) {
  const {
    ownerAvatarUrl,
    isPrivate,
    isTemplate,
    isFork,
    isMirror,
    visibilityLabel,
    parentRepo,
    mirrorUrl,
    templateSource,
  } = repo
  const labelVariant = visibilityLabel === 'Public archive' ? 'attention' : 'secondary'
  const getHovercardAttributes = useGetHovercardAttributesForType()

  return (
    <div data-testid="repo-title">
      <div className="d-flex flex-items-center f3 text-normal min-width-0 overflow-hidden">
        <RepoLeadingVisual
          ownerLogin={ownerLogin}
          ownerAvatarUrl={ownerAvatarUrl}
          isPrivate={isPrivate}
          isTemplate={isTemplate}
        />
        <Truncate inline maxWidth="100%" title={repoName} itemProp="name" data-testid="repo-name-wrapper">
          <strong>
            <Link as={RouterLink} to={paths.repoUrl} className="color-fg-default mr-2" data-testid="repo-name-link">
              {repoName}
            </Link>
          </strong>
        </Truncate>
        <Label variant={labelVariant} className="mr-1 v-align-middle" data-testid="repo-visibility-label">
          {isPrivate && <LockIcon size={12} className="pl-2 mr-1" />}
          {isMirror && !isPrivate && <MirrorIcon size={12} className="pl-2 mr-1" />}
          {visibilityLabel}
        </Label>
      </div>
      {isFork && parentRepo && (
        <div className="show-whenRegular">
          <span className="text-small lh-condensed-ultra no-wrap mt-1" data-testid="fork-parent">
            <span>forked from </span>
            <Link
              as={RouterLink}
              to={parentRepo.url}
              {...getHovercardAttributes('repository', {
                owner: parentRepo.ownerLogin,
                repo: parentRepo.name,
              })}
            >
              {parentRepo.ownerLogin}/{parentRepo.name}
            </Link>
          </span>
        </div>
      )}
      {isMirror && mirrorUrl && (
        <div className="show-whenRegular">
          <div
            className="d-inline-flex flex-items-baseline text-small lh-condensed-ultra no-wrap mt-1"
            data-testid="mirror-source"
          >
            <span>mirrored from </span>
            <Truncate as={Link} href={mirrorUrl} inline muted title={mirrorUrl} maxWidth={600}>
              {mirrorUrl}
            </Truncate>
          </div>
        </div>
      )}
      {!isFork && !isMirror && templateSource && (
        <div className="show-whenRegular">
          <span className="text-small lh-condensed-ultra no-wrap mt-1" data-testid="template-source">
            <span>generated from </span>
            <Link
              as={RouterLink}
              to={templateSource.url}
              inline
              muted
              {...getHovercardAttributes('repository', {
                owner: templateSource.ownerLogin,
                repo: templateSource.name,
              })}
            >
              {templateSource.ownerLogin}/{templateSource.name}
            </Link>
          </span>
        </div>
      )}
    </div>
  )
}
